import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SingleAuction from "./singleAuction";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLanguage } from "../context/LanguageContext";
import translations from "../locals/translations.json";
import axios from "axios";
import usePageMeta from "../hooks/usePageMeta.js";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

const Auctions = () => {
  usePageMeta("auctions");
  const auctionInfoRef = useRef(null);

  const { language } = useLanguage();
  const [currentDate, setCurrentDate] = useState(dayjs().tz(DUBAI_TZ));
  const [events, setEvents] = useState([]);
  const [todayAuction, setTodayAuction] = useState(null);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const [liveAuction, setLiveAuction] = useState([]);
  const [AuctionLaneStatus, setAuctionLaneStatus] = useState([]);
  const toDubaiDateStr = (dateStr) => {
    if (!dateStr) return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    try {
      return dayjs(dateStr).tz(DUBAI_TZ).format("YYYY-MM-DD");
    } catch (error) {
      console.warn("Error formatting date:", dateStr, error);
      return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    }
  };

  // Fetch auctions
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const res2 = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              page: 1,
              limit: 50,
              orderBy: "startDate",
              order: "ASC",
              search: "",
              slug: "auctions",
              type: "live",
              filters: {
                unSoldVehicles: 1,
              },
            },
          }
          // http://3.28.71.76:5000/api/auctions/paginated?page=1&limit=50&orderBy=id&order=DESC&search=&slug=auctions&type=upcomming
        );

        console.log("res2", res2);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              type: "upcomming,live",
              orderBy: "startDate",
              order: "ASC",
              filters: {
                isActive: true,
                unSoldVehicles: 1,
              },
            },
          }
          // http://3.28.71.76:5000/api/auctions/paginated?page=1&limit=50&orderBy=id&order=DESC&search=&slug=auctions&type=upcomming
        );

        const auctions = res.data.data;

        // const LiveAuction =auctions || [];
        setLiveAuction(auctions);
        // Create a map of auction ID to lane statuses
        const laneStatusMap = {};
        auctions.forEach((auction) => {
          if (auction.auctionLanes) {
            laneStatusMap[auction.id] = auction.auctionLanes.map((lane) => ({
              laneId: lane.id || lane.laneId,
              status:
                typeof lane.auctionStatus === "string"
                  ? lane.auctionStatus
                  : lane.auctionStatus?.name ||
                    lane.auctionStatus?.slug ||
                    lane.status,
            }));
          }
        });
        setAuctionLaneStatus(laneStatusMap);
        // Map auctions for UI (preserve auctionLanes and vehicles)
        const mapped = auctions.map((a) => {
          // Ensure we have valid auction data
          if (!a || typeof a !== "object") {
            console.warn("Invalid auction data:", a);
            return null;
          }

          // Ensure auction name is a string
          let auctionName = `Auction ${a.id}`;
          if (typeof a.name === "string" && a.name.trim()) {
            auctionName = a.name.trim();
          } else if (typeof a.auction === "string" && a.auction.trim()) {
            auctionName = a.auction.trim();
          }
          const auctionLanes =
            a.auctionLanes
              ?.map((lane, idx) => {
                // Ensure lane is a valid object
                if (!lane || typeof lane !== "object") {
                  console.warn("Invalid lane data:", lane);
                  return null;
                }

                const vehicles = lane.auctionLaneVehicles || [];
                const laneName = lane.lane?.name || lane.name;

                // Find the status for this specific lane
                const mappedStatus = AuctionLaneStatus[a.id]?.find(
                  (status) => status.laneId === (lane.id || lane.laneId)
                )?.status;

                const laneStatus =
                  mappedStatus ||
                  (typeof lane.auctionStatus === "string"
                    ? lane.auctionStatus
                    : lane.auctionStatus?.name || lane.auctionStatus?.slug) ||
                  (typeof lane.status === "string"
                    ? lane.status
                    : lane.status?.name || lane.status?.slug) ||
                  "unknown";

                return {
                  id:
                    typeof lane.id === "number" || typeof lane.id === "string"
                      ? lane.id
                      : idx,
                  name:
                    typeof laneName === "string" ? laneName : `Lane ${idx + 1}`,
                  vehicles,
                  status: laneStatus, // Add status to each lane
                  totalVehicles: Array.isArray(vehicles) ? vehicles.length : 0,
                };
              })
              .filter((lane) => lane !== null) || [];

          const totalCars = auctionLanes.reduce(
            (sum, lane) =>
              sum +
              (typeof lane?.totalVehicles === "number"
                ? lane.totalVehicles
                : 0),
            0
          );

          return {
            id: a.id,
            auction: auctionName,
            name: a.name, // Keep original name property
            date: toDubaiDateStr(a.startDate),
            startDate: a.startDate || new Date().toISOString(),
            branch: a.branch || null,
            status: a.status, // Pass through auction status
            type: a.type, // Pass through auction type
            auctionLanes, // keep lanes with vehicles (now includes status for each lane)
            totalCars,
          };
        });

        // Filter out any null entries and auctions with 0 vehicles
        const validMapped = mapped.filter(
          (auction) => auction !== null && auction.totalCars > 0
        );

        // Additional validation to ensure all required properties are strings/numbers
        const validatedMapped = validMapped.map((auction) => {
          if (typeof auction.auction !== "string") {
            console.warn(
              "Invalid auction name type:",
              typeof auction.auction,
              auction.auction
            );
            return {
              ...auction,
              auction: `Auction ${auction.id}`,
            };
          }
          if (typeof auction.totalCars !== "number") {
            console.warn(
              "Invalid totalCars type:",
              typeof auction.totalCars,
              auction.totalCars
            );
            return {
              ...auction,
              totalCars: 0,
            };
          }
          return auction;
        });

        setEvents(validatedMapped);

        // Find the most recent auction (either past or future) using Dubai timezone
        const now = dayjs().tz(DUBAI_TZ);
        let nextAuction = null;
        let minDiff = Infinity;

        validatedMapped.forEach((a) => {
          if (!a.startDate) return;
          try {
            const auctionTime = dayjs.utc(a.startDate).tz(DUBAI_TZ);
            const diff = auctionTime.diff(now);
            // Include both past and future auctions, prioritize future ones
            if (diff >= 0 && diff < minDiff) {
              minDiff = diff;
              nextAuction = a;
            } else if (nextAuction === null && diff < 0) {
              // If no future auction found, use the most recent past auction
              const pastDiff = Math.abs(diff);
              if (pastDiff < minDiff) {
                minDiff = pastDiff;
                nextAuction = a;
              }
            }
          } catch (error) {
            console.warn("Error processing auction time:", a.startDate, error);
          }
        });

        // Auto-select first auction on initial load if no auction is selected
        if (!selectedAuction && auctions.length > 0) {
          setSelectedAuction(auctions[0]);
        } else {
          setSelectedAuction(nextAuction);
        }
      } catch (err) {
        console.error("Error fetching auctions", err);
      }
    };

    fetchAuctions();
  }, []);

  // Calendar weeks
  const weeks = useMemo(() => {
    const year = currentDate.year();
    const month = currentDate.month(); // 0-11

    const startOfMonth = dayjs.tz(`${year}-${month + 1}-01`, DUBAI_TZ);
    const endOfMonth = startOfMonth.endOf("month");
    const startDay = startOfMonth.day();
    const daysInMonth = endOfMonth.date();

    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);

    for (let d = 1; d <= daysInMonth; d++) {
      days.push(dayjs.tz(`${year}-${month + 1}-${d}`, DUBAI_TZ));
    }

    const weeksArr = [];
    for (let i = 0; i < days.length; i += 7)
      weeksArr.push(days.slice(i, i + 7));
    return weeksArr;
  }, [currentDate]);

  const prevMonth = () => setCurrentDate((prev) => prev.subtract(1, "month"));
  const nextMonth = () => setCurrentDate((prev) => prev.add(1, "month"));

  // Group auctions by date
  const auctionsByDate = useMemo(() => {
    const grouped = {};
    events.forEach((auction) => {
      if (auction && auction.date) {
        if (!grouped[auction.date]) {
          grouped[auction.date] = [];
        }
        grouped[auction.date].push(auction);
      }
    });
    return grouped;
  }, [events]);

  // Handle mouse enter for popup
  const handleMouseEnter = (event, dateStr) => {
    const auctionsOnDate = auctionsByDate[dateStr];
    if (auctionsOnDate && auctionsOnDate.length > 1) {
      const rect = event.currentTarget.getBoundingClientRect();
      setPopupPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
      setHoveredDate(dateStr);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    // Only close if popup is not being hovered
    if (!isPopupHovered) {
      setHoveredDate(null);
    }
  };

  // Handle click outside popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hoveredDate &&
        !event.target.closest(".auction-popup") &&
        !isPopupHovered
      ) {
        setHoveredDate(null);
      }
    };

    if (hoveredDate) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [hoveredDate, isPopupHovered]);

  // Prioritize user selection (selectedAuction), then LiveAuction, then todayAuction
  const activeAuction =
    selectedAuction ||
    (liveAuction && liveAuction.length > 0 ? liveAuction[0] : null) ||
    todayAuction;

  // helper for closed check (use Dubai tz)
  const isClosed =
    !!activeAuction &&
    activeAuction.startDate &&
    dayjs
      .utc(activeAuction.startDate)
      .tz(DUBAI_TZ)
      .isBefore(dayjs().tz(DUBAI_TZ));

  return (
    <section className="max-w-7xl mx-auto">
      {/* Calendar & Auctions */}
      <div className="p-6 bg-[#FFF9F9]">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Auction Info */}
          <div className="w-full lg:w-[470px]">
            {activeAuction && (activeAuction.auction || activeAuction.name) ? (
              <SingleAuction
                auction={{
                  ...activeAuction,
                  laneStatusMap: AuctionLaneStatus,
                }}
                isSelected={true}
                onSelect={setSelectedAuction}
                index={0}
              />
            ) : (
              <div className="p-6 text-center text-gray-500">
                No auction today. {translations[language].noAuctionToday}
              </div>
            )}
          </div>

          {/* Calendar */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-[#46464633]">
              <div className="text-center py-4 font-bold text-lg bg-gray-100 text-[#0B0C2B]">
                {translations[language].upcomingAuctions}
              </div>
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-900 text-[14px] md:text-xl">
                    {translations[language].auctionCalendar}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevMonth}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className="text-[14px] md:text-xl">
                      {
                        translations[language].months[
                          currentDate.tz(DUBAI_TZ).format("MMMM").toLowerCase()
                        ]
                      }{" "}
                      {currentDate.tz(DUBAI_TZ).year()}
                    </span>
                    <button
                      onClick={nextMonth}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {weeks.map((week, wIdx) => (
                    <div key={wIdx} className="grid grid-cols-7 gap-4">
                      {week.map((day, dIdx) => {
                        if (!day)
                          return (
                            <div
                              key={dIdx}
                              className="p-4 rounded-lg bg-gray-50 border border-gray-200"
                            />
                          );

                        const dateStr = day.format("YYYY-MM-DD");
                        const event = events.find(
                          (e) => e && e.date === dateStr
                        );
                        const auctionsOnDate = auctionsByDate[dateStr] || [];
                        const hasMultipleAuctions = auctionsOnDate.length > 1;
                        const todayStr = dayjs()
                          .tz(DUBAI_TZ)
                          .format("YYYY-MM-DD");
                        const isToday = dateStr === todayStr;

                        return (
                          <div
                            key={dIdx}
                            onClick={() => {
                              if (event && (event.auction || event.name)) {
                                setSelectedAuction(event);
                                auctionInfoRef.current?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }
                            }}
                            onMouseEnter={(e) => handleMouseEnter(e, dateStr)}
                            onMouseLeave={handleMouseLeave}
                            className={`relative p-1 md:p-4 rounded-lg text-center border cursor-pointer ${
                              isToday
                                ? "bg-green-100 border-green-500"
                                : event
                                ? "bg-[#FCE8E1] border-[#F15A29]"
                                : "bg-gray-50 border-gray-200"
                            } hover:shadow-md transition-shadow`}
                          >
                            {/* Day Number */}
                            <p className="text-gray-900 font-semibold text-[9px] md:text-lg">
                              {translations[language]?.numbers?.[
                                day.format("D")
                              ] || day.format("D")}
                            </p>

                            {/* Short Day Name */}
                            <p className="text-gray-500 text-[7px] md:text-sm mb-2">
                              {translations[language].days?.[
                                day.format("ddd").toLowerCase()
                              ] || day.format("ddd")}
                            </p>

                            {/* Desktop auction info */}
                            {event && (event.auction || event.name) && (
                              <div className="hidden md:block">
                                <p className="text-[10px] md:text-xs text-gray-700 font-medium">
                                  {hasMultipleAuctions ? (
                                    <span className="flex items-center justify-center gap-1">
                                      {auctionsOnDate.length}{" "}
                                      {translations[language]?.auctions ||
                                        "Auctions"}
                                      <span className="text-[#F15A29]">•</span>
                                    </span>
                                  ) : (
                                    event.auction || event.name
                                  )}
                                </p>
                                <p className="text-[10px] md:text-xs pt-[3px] text-gray-600">
                                  {hasMultipleAuctions
                                    ? auctionsOnDate.reduce(
                                        (sum, a) => sum + (a.totalCars || 0),
                                        0
                                      )
                                    : event.totalCars || 0}{" "}
                                  {translations[language].cars}
                                </p>
                              </div>
                            )}

                            {/* Visual indicators */}
                            {event && (
                              <div className="flex justify-center items-center gap-1 mt-1">
                                {/* Orange dot for mobile */}
                                <motion.div
                                  className="md:hidden w-2 h-2 bg-[#F15A29] rounded-full"
                                  animate={{
                                    scale: [1, 1.4, 1],
                                    y: [0, -3, 0],
                                  }}
                                  transition={{
                                    duration: 0.8,
                                    repeat: Infinity,
                                  }}
                                />

                                {/* Multiple auctions indicator */}
                                {hasMultipleAuctions && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-[8px] md:text-[10px] text-[#F15A29] font-bold">
                                      {auctionsOnDate.length}
                                    </span>
                                    <span className="text-[8px] md:text-[10px] text-[#F15A29]">
                                      •
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Multiple Auctions Popup */}
      {hoveredDate && auctionsByDate[hoveredDate] && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 10 }}
          transition={{ duration: 0.2 }}
          className="auction-popup fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[280px] max-w-[400px] w-auto"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            transform: "translateX(-50%) translateY(-100%)",
          }}
          onMouseEnter={() => setIsPopupHovered(true)}
          onMouseLeave={() => {
            setIsPopupHovered(false);
            // Small delay to ensure smooth interaction
            setTimeout(() => {
              setHoveredDate(null);
            }, 150);
          }}
        >
          {/* Arrow pointing down */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
            <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200"></div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <div className="text-center flex-1">
                <h4 className="font-semibold text-gray-900 text-sm">
                  {dayjs(hoveredDate).format("MMMM D, YYYY")}
                </h4>
                <p className="text-xs text-gray-500">
                  {auctionsByDate[hoveredDate].length}{" "}
                  {translations[language]?.auctions || "Auctions"}
                </p>
              </div>
              <button
                onClick={() => setHoveredDate(null)}
                className="ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                title={translations[language]?.close || "Close"}
              >
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div
              className={`space-y-2 ${
                auctionsByDate[hoveredDate].length > 4
                  ? "max-h-64 overflow-y-auto"
                  : ""
              }`}
            >
              {auctionsByDate[hoveredDate].map((auction, index) => (
                <motion.div
                  key={auction.id || index}
                  className={`p-3 rounded-md cursor-pointer transition-all duration-200 ${
                    selectedAuction?.id === auction.id
                      ? "bg-[#F15A29] text-white"
                      : "bg-gray-50 hover:bg-gray-100 hover:shadow-md"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedAuction(auction);
                    setHoveredDate(null);
                    auctionInfoRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium break-words ${
                          selectedAuction?.id === auction.id
                            ? "text-white"
                            : "text-gray-900"
                        }`}
                      >
                        {auction.auction || auction.name}
                      </p>
                      <p
                        className={`text-xs ${
                          selectedAuction?.id === auction.id
                            ? "text-gray-200"
                            : "text-gray-600"
                        }`}
                      >
                        {dayjs
                          .utc(auction.startDate)
                          .tz(DUBAI_TZ)
                          .format("hh:mm A")}
                      </p>
                      {selectedAuction?.id === auction.id && (
                        <p className="text-xs text-gray-200 mt-1">
                          {translations[language]?.selected || "Selected"}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p
                        className={`text-xs whitespace-nowrap ${
                          selectedAuction?.id === auction.id
                            ? "text-gray-200"
                            : "text-gray-500"
                        }`}
                      >
                        {auction.totalCars || 0}{" "}
                        {translations[language]?.cars || "cars"}
                      </p>
                      {selectedAuction?.id === auction.id && (
                        <div className="w-2 h-2 bg-white rounded-full mt-1 ml-auto"></div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Upcoming Auctions Grid
      {events.filter((e) => e && e.auction).length > 0 && (
        <div className="p-6 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-white text-3xl font-bold mb-4">
                {translations[language]?.upcomingAuctions ||
                  "Upcoming Auctions"}
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                {translations[language]?.upcomingAuctionsDescription ||
                  "Explore our upcoming auctions and plan your participation. Each auction features multiple lanes with quality vehicles."}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {events
                .slice(0, 6)
                .map((auction, index) =>
                  auction && (auction.auction || auction.name) ? (
                    <SingleAuction
                      key={auction.id || index}
                      auction={auction}
                      isSelected={selectedAuction?.id === auction.id}
                      onSelect={setSelectedAuction}
                    />
                  ) : null
                )}
            </div>

            {events.filter((e) => e && e.auction).length > 6 && (
              <div className="text-center mt-8">
                <button className="bg-[#F15A29] hover:bg-[#D14A1F] text-white px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105">
                  {translations[language]?.viewAllAuctions ||
                    "View All Auctions"}
                </button>
              </div>
            )}
          </div>
        </div>
      )} */}
    </section>
  );
};

export default Auctions;
